import { Check, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import type { JSX, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
export const totpEnrollmentSchema = z.object({
  totpURI: z.string().startsWith("otpauth://totp/"),
  backupCodes: z.array(z.string()).min(1),
});

const Step = ({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: ReactNode;
}): JSX.Element => (
  <li className="flex gap-3">
    <span
      aria-hidden
      className="mt-px flex size-6 shrink-0 items-center justify-center rounded-full bg-v3-surface-3 text-[12px] font-semibold tabular-nums text-v3-text-2"
    >
      {n}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-semibold text-v3-text">{title}</p>
      <div className="mt-2">{children}</div>
    </div>
  </li>
);

export function NativeTotpEnrollment({
  enrollment,
  saved,
  onSaved,
}: {
  enrollment: z.infer<typeof totpEnrollmentSchema>;
  saved: boolean;
  onSaved: (saved: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const secret = new URL(enrollment.totpURI).searchParams.get("secret");
  return (
    <ol className="flex flex-col gap-5 font-v3">
      <Step n={1} title={t("auth.native.totp.scan")}>
        {/* QR codes need a light background to scan, also in dark mode. */}
        <div className="inline-block rounded-xl bg-white p-2 shadow-v3-hairline">
          <QRCodeSVG
            value={enrollment.totpURI}
            size={160}
            marginSize={1}
            title={t("auth.native.totp.qrTitle")}
          />
        </div>
        <details className="mt-2 text-[13px] text-v3-text-2">
          <summary className="cursor-pointer select-none font-medium hover:text-v3-text">
            {t("auth.native.totp.setupKey")}
          </summary>
          <code className="mt-2 block break-all rounded-lg bg-v3-surface-2 px-3 py-2 font-mono text-[13px] text-v3-text">
            {secret}
          </code>
        </details>
      </Step>
      <Step n={2} title={t("auth.native.totp.saveCodes")}>
        <p className="text-[13px] text-v3-text-2">
          {t("auth.native.totp.saveCodesHint")}
        </p>
        <div className="mt-2 max-w-sm rounded-xl bg-v3-surface-2 p-3 shadow-v3-hairline">
          <pre className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[13px] text-v3-text">
            {enrollment.backupCodes.map((code) => (
              <span key={code}>{code}</span>
            ))}
          </pre>
          <button
            className="mt-2 inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-medium text-v3-text-2 transition-colors hover:bg-v3-surface-3 hover:text-v3-text"
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(enrollment.backupCodes.join("\n"))
                .then(() => setCopied(true))
                .catch(() => {});
            }}
          >
            {copied ? (
              <Check aria-hidden className="size-3.5" />
            ) : (
              <Copy aria-hidden className="size-3.5" />
            )}
            {copied ? t("auth.native.totp.copied") : t("auth.native.totp.copy")}
          </button>
        </div>
        <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-[14px] text-v3-text">
          <input
            checked={saved}
            className="size-4 accent-v3-brand"
            type="checkbox"
            onChange={(event) => onSaved(event.target.checked)}
          />
          {t("auth.native.totp.savedConfirm")}
        </label>
      </Step>
    </ol>
  );
}
